import WebFont from "webfontloader";
import { isNull } from "../../../../../lib/html5-software-library/src/ranj/util/TypeUtil";

/**
 * Uses [WebFontLoader](https://github.com/typekit/webfontloader) to load a given array of Fonts families in [FVD format](https://github.com/typekit/fvd)
 * 
 * Note on loading process:
 * - The WebFontLoader does not download the font asset directly, but triggers it to be loaded via HTML and informs when it is ready.
 * - Therefore, if a font is misconfigured (e.g, wrong family name/style) it only detects an error when the timeout is reached.
 * 
 * 
 * Current limitations:
 * - does not provide loading progress.
 * - current implementation only allows to load `@font-face` included in an already loaded stylesheet.
 */
export default class FontLoaderService {
    /**
     * Triggers and waits for given fonts to load.
     * 
     * Note: the supplied fonts in [FVD format](https://github.com/typekit/webfontloader) must match the `@font-face` in a loaded stylesheet
     * 
     * @param {string[]} fonts fonts in [FVD format](https://github.com/typekit/webfontloader)
     * @param {{ timeoutMs: number, verbose: boolean }} options optional values for loading
     * @returns {Promise<FontLoaderResult>} Promise with result of fonts that failed/succeeded.
     */
    load(fonts, options = FontLoaderService.DEFAULT_LOAD_OPTIONS){
        const timeout = isNull(options.timeoutMs) ? FontLoaderService.DEFAULT_LOAD_OPTIONS.timeoutMs : options.timeoutMs;
        const verbose = isNull(options.verbose) ? FontLoaderService.DEFAULT_LOAD_OPTIONS.verbose : options.verbose;
        
        const result = new FontLoaderResult();

        return new Promise((resolve, reject) => {
            // See: https://github.com/typekit/webfontloader#configuration
            WebFont.load({
                // NB: 'custom' module is used to load fonts based on the loaded stylesheets 
                // See: https://github.com/typekit/webfontloader#custom
                custom: {
                    // Note: if no FVD style is provided with the font, it defaults to `n4`
                    families: fonts,
                },
                // NB: timeout per font in milliseconds. See: https://github.com/typekit/webfontloader#timeouts
                timeout: timeout,

                active: () => {
                    if(verbose) {
                        console.info(`[FontLoaderService] Fonts loaded!`);
                    }
                    resolve(result);
                },

                inactive: () => {
                    if(verbose) {
                        console.error(`[FontLoaderService] Failed to load fonts ${fonts} or timeout has triggered.`);
                    }
                    reject(new FontLoaderError(result));
                },

                fontactive: (fontFamily, fvd) => {
                    const font = `${fontFamily}:${fvd}`;
                    result.success.push(font);
                    if(verbose) {
                        console.info(`[FontLoaderService] Font active: ${font}`);
                    }
                },

                fontinactive: (fontFamily, fvd) => {
                    const font = `${fontFamily}:${fvd}`;
                    result.fail.push(font);
                    if(verbose) {
                        console.warn(`[FontLoaderService] Failed to load font: ${font}`);
                    }
                }
            });
        });
    }
}

export class FontLoaderResult {
    constructor() {
        this.success = [];
        this.fail = [];
    }
}

export class FontLoaderError {
    /**
     * 
     * @param {FontLoaderResult} result 
     */
    constructor(result) {
        this.result = result;
    }
}

FontLoaderService.DEFAULT_LOAD_OPTIONS = {
    timeoutMs: 10000,
    verbose: false
};